/**
 * Hook for managing competitors data in the Knowledge Graph widget.
 *
 * This hook fetches and organizes competitor data from multiple sources:
 * - Company competitors with themes (from ontology API)
 * - Screener data (overview + performance metrics)
 * - Entity details for each competitor
 *
 * Data mapping process:
 * - Screener data is positionally mapped to entities (entities[0] → data[0])
 * - Data is combined by entity tag for both table and drawer access
 *
 * The data is organized into:
 * - tableData: Array of competitor rows for the table display
 * - competitorsByTag: Map of competitor data keyed by entity tag for drawer access
 *
 * @param entityTag - The entity tag to fetch competitors for
 * @returns Object containing table data, competitor map, loading states, and metadata
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import {
  CompanyCompetitorsWithThemesResponse,
  Entity,
  FilterResults,
  screener,
  ThemeSchemaResponse,
} from '@toggle/toggle';
import { useMemo } from 'react';

import { useEntities } from '~/hooks/use-entities';
import { useCompanyCompetitorWithThemes } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/app-paths';
import { wretchRequest } from '~/utils/api-fetch/apiFetch';
import { QUERY_KEY_SCREENER_RESULTS } from '~/views/screener/results/useScreenerTable';
import {
  extractPerfKGData,
  extractScreenerOverviewData,
} from '~/views/theme-overview/tab-content/utils';

const PAGE_SIZE = 200;

export type CompetitorData = {
  entity: Entity;
  competitorData: CompanyCompetitorsWithThemesResponse[0];
  competingThemes: ThemeSchemaResponse[];
};

export type CompetitorsByTag = Record<string, CompetitorData>;

export type CompetitorsTableData = {
  entity?: Entity;
  hasData: boolean;
  competingThemes: ThemeSchemaResponse[];
  rank: number;
} & Partial<Record<string, unknown>>;

export const useCompetitorsData = ({ entityTag }: { entityTag: string }) => {
  const { params } = useQueryParams();
  const selectedCompetitor = params[queryKeys.competitorTag];

  const { data: competitorsData } = useCompanyCompetitorWithThemes({
    entityTag,
  });

  const entityTags = competitorsData
    ? competitorsData.map(item => item.company)
    : [];

  const requestFilters = {
    entity_tag: {
      values: [{ tags: entityTags }],
    },
  };

  const {
    data: screenerData,
    isFetching,
    isError,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEY_SCREENER_RESULTS, requestFilters],
    queryFn: async ({ signal, pageParam }) => {
      return await wretchRequest<FilterResults>(`${screener.postFilter.path}`, {
        method: 'post',
        body: {
          filters: requestFilters,
          page: {
            number: pageParam,
            size: PAGE_SIZE,
          },
        },
        signal,
      });
    },
    enabled: !!entityTags.length,
    initialPageParam: 1,
    staleTime: getTimeTillEndOfDayMs(),
    getNextPageParam: lastData => lastData.page.next,
  });

  const entities = screenerData?.pages?.[0]?.entities;
  const latestData = screenerData?.pages?.[0];

  const { data: entitiesData, isLoading: isEntitiesLoading } = useEntities({
    entityTags: entities ?? [],
    appendFetching: true,
    enabled: !isFetching || !isError,
  });

  const { columnsMeta, combinedScreenerData } = useMemo(() => {
    const screenerOverview = extractScreenerOverviewData(latestData?.data);
    const screenerPerf = extractPerfKGData(latestData?.data);

    const combinedScreenerData = [
      ...screenerOverview.data,
      ...screenerPerf.data,
    ];
    const columnsMeta = [...screenerOverview.meta, ...screenerPerf.meta];

    return { columnsMeta, combinedScreenerData };
  }, [latestData]);

  const screenerByTag = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    entities?.forEach((entityTag, index) => {
      map.set(entityTag, combinedScreenerData[index] || {});
    });
    return map;
  }, [entities, combinedScreenerData]);

  const organisedData = useMemo(() => {
    if (!competitorsData || !combinedScreenerData || !entitiesData) {
      return null;
    }

    const competitorsByTag: CompetitorsByTag = {};
    const tableData: CompetitorsTableData[] = [];

    competitorsData.forEach(competitor => {
      const entity = entitiesData.find(e => e.tag === competitor.company);

      if (entity) {
        const normalizedTheme = competitor.competing_themes.map(theme => ({
          ...theme,
          parent_theme: theme.parent,
        }));

        competitorsByTag[competitor.company] = {
          entity,
          competitorData: competitor,
          competingThemes: normalizedTheme,
        };

        tableData.push({
          entity,
          hasData: !isEntitiesLoading && !!entity,
          competingThemes: normalizedTheme,
          rank: competitor.relationship.rank,
          ...(screenerByTag.get(competitor.company) || {}),
        });
      }
    });

    return {
      tableData,
      columnsMeta,
      competitorsByTag,
      selectedCompetitor,
    };
  }, [
    competitorsData,
    entitiesData,
    combinedScreenerData,
    columnsMeta,
    selectedCompetitor,
  ]);

  return {
    tableData: organisedData?.tableData ?? [],
    competitorsByTag: organisedData?.competitorsByTag ?? {},
    selectedCompetitor,
    columnsMeta,
    isError,
    isFetching,
  };
};
